import { Play, Pause, Maximize2, Monitor, PanelRightOpen, LayoutGrid, FileText } from 'lucide-react'
import { useEditor } from '../contexts/EditorContext'

interface PreviewPanelProps {
  isAssistantOpen?: boolean
  onToggleAssistant?: () => void
  isAssetsPanelOpen?: boolean
  onToggleAssetsPanel?: () => void
  isScriptPanelOpen?: boolean
  onToggleScriptPanel?: () => void
}

export default function PreviewPanel({
  isAssistantOpen = true,
  onToggleAssistant,
  isAssetsPanelOpen = true,
  onToggleAssetsPanel,
  isScriptPanelOpen = true,
  onToggleScriptPanel,
}: PreviewPanelProps) {
  const { isPlaying, setIsPlaying, currentTime, duration } = useEditor()

  // Format time for display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    const centisecs = Math.floor((seconds % 1) * 100)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${centisecs.toString().padStart(2, '0')}`
  }

  const formattedCurrentTime = formatTime(currentTime)
  const formattedDuration = formatTime(duration)

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      {/* Left Toggles (Assets & Script) */}
      <div className="absolute left-3 top-3 z-10 flex gap-2">
        {!isAssetsPanelOpen && (
          <button
            onClick={onToggleAssetsPanel}
            className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:bg-gray-50 hover:text-gray-900"
            title="Open Assets"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
        )}
        {!isScriptPanelOpen && (
          <button
            onClick={onToggleScriptPanel}
            className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:bg-gray-50 hover:text-gray-900"
            title="Open Script"
          >
            <FileText className="h-4 w-4" />
          </button>
        )}
      </div>

      {!isAssistantOpen && (
        <button
          onClick={onToggleAssistant}
          className="absolute right-3 top-3 z-10 rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:bg-gray-50 hover:text-gray-900"
          title="Open Assistant"
        >
          <PanelRightOpen className="h-4 w-4" />
        </button>
      )}

      {/* Preview Area */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
        <div className="relative aspect-video w-full max-w-3xl overflow-hidden rounded-xl bg-black shadow-2xl ring-1 ring-black/10">
          <img
            src="https://images.unsplash.com/photo-1580128660010-fd027e1e587a?w=1200&h=675&fit=crop"
            className="h-full w-full object-cover opacity-90"
            alt="Preview"
          />
          <div className="absolute inset-0 flex items-end justify-center pb-8">
            <div className="rounded-lg bg-black/50 px-4 py-2 text-lg font-medium text-white shadow-lg backdrop-blur-sm">
              小明啊
            </div>
          </div>

          {/* Name Plate */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 transform">
            <div className="rounded border-2 border-[#d4b483] bg-[#2a1b15] px-6 py-1 shadow-lg">
              <span className="font-serif text-sm font-bold tracking-widest text-[#d4b483]">
                DARK H. WEELT
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 flex w-full items-center justify-center">
        <div className="flex w-full max-w-3xl items-center justify-between px-2">
          <span className="font-mono text-sm font-medium text-gray-500">
            {formattedCurrentTime} / {formattedDuration}
          </span>

          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-900 text-white shadow-md transition-all hover:scale-105 hover:bg-gray-800 active:scale-95"
          >
            {isPlaying ? (
              <Pause className="h-5 w-5 fill-current" />
            ) : (
              <Play className="ml-0.5 h-5 w-5 fill-current" />
            )}
          </button>

          <div className="flex items-center gap-4">
            <button className="text-gray-400 transition-colors hover:text-gray-900">
              <Maximize2 className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              <Monitor className="h-4 w-4" />
              <span>16:9</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
